<template>
  <div class="not-found-page">
    <!-- Standard 404 content -->
    <div v-if="!playing" class="error-content">
      <h1 class="error-code">404</h1>

      <p class="error-message">The page you're looking for doesn't exist.</p>
      <p class="error-detail text-surface-500 dark:text-surface-400">
        It may have been moved, deleted, or you may have mistyped the URL.
      </p>
      <p class="support-hint text-surface-500 dark:text-surface-400">
        Something broken? Let us know with an
        <a
          href="https://withreach.atlassian.net/jira/software/c/projects/INSUP/boards/27"
          target="_blank"
          rel="noopener noreferrer"
          class="support-link"
          >INSUP ticket</a
        >.
      </p>
      <div class="error-actions">
        <router-link to="/" class="home-btn"> Go home </router-link>
        <button
          class="play-btn"
          data-test-id="play-game-btn"
          @click="startGame"
        >
          Play a game instead?
        </button>
      </div>
    </div>

    <!-- CRT TV Game (easter egg) -->
    <Transition name="tv-on">
      <div v-if="playing" class="game-wrapper">
        <div class="tv-setup">
          <div class="crt-body">
            <div class="crt-top-vent">
              <span v-for="n in 12" :key="n" class="vent-slot" />
            </div>
            <div class="crt-inner">
              <div class="screen-well">
                <div class="screen-bezel">
                  <div class="screen-glass">
                    <div class="scanlines" />
                    <div class="screen-curve" />
                    <div class="screen-flicker" />
                    <iframe
                      ref="gameFrame"
                      src="/games/payments-threats.html"
                      class="game-screen"
                      title="Error Hunter - 404 Game"
                      @load="onFrameLoad"
                    />
                  </div>
                </div>
              </div>
              <div class="side-panel">
                <div class="brand-area">
                  <span class="brand-name">ERROR</span>
                  <span class="brand-model">404</span>
                </div>
                <div class="channel-knob">
                  <div class="knob-outer">
                    <div class="knob-inner">
                      <div class="knob-line" />
                    </div>
                  </div>
                  <span class="knob-label">CH</span>
                </div>
                <div class="channel-knob">
                  <div class="knob-outer knob-small">
                    <div class="knob-inner">
                      <div class="knob-line" />
                    </div>
                  </div>
                  <span class="knob-label">VOL</span>
                </div>
                <div class="power-area">
                  <div class="power-led" />
                  <span class="knob-label">POWER</span>
                </div>
              </div>
            </div>
          </div>
          <div class="tv-legs">
            <div class="leg leg-left" />
            <div class="leg leg-right" />
          </div>
        </div>

        <div class="game-footer">
          <button
            class="back-btn"
            data-test-id="back-to-404-btn"
            @click="stopGame"
          >
            &larr; Back
          </button>
          <router-link to="/" class="home-link"> Go home </router-link>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onBeforeUnmount } from 'vue'
import { useTitle } from '@vueuse/core'

useTitle('404 — Page Not Found')

const playing = ref(false)

const gameFrame = ref<HTMLIFrameElement>()

const onFrameLoad = () => {
  gameFrame.value?.focus()
}

const handleClick = () => {
  if (playing.value) {
    gameFrame.value?.focus()
  }
}

const startGame = () => {
  playing.value = true
  document.addEventListener('click', handleClick)
  nextTick(() => {
    gameFrame.value?.focus()
  })
}

const stopGame = () => {
  playing.value = false
  document.removeEventListener('click', handleClick)
}

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClick)
})
</script>

<style scoped>
.not-found-page {
  min-height: calc(100vh - 4rem);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

/* === STANDARD 404 === */
.error-content {
  text-align: center;
  max-width: 480px;
}

.error-code {
  font-size: 6rem;
  font-weight: 700;
  line-height: 1;
  color: var(--p-surface-300);
  margin: 0 0 0.5rem;
}

:root.dark .error-code {
  color: var(--p-surface-700);
}

.error-message {
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--p-surface-800);
  margin: 0 0 0.5rem;
}

:root.dark .error-message {
  color: var(--p-surface-200);
}

.error-detail {
  font-size: 0.875rem;
  margin: 0 0 0.75rem;
}

.support-hint {
  font-size: 0.8125rem;
  margin: 0 0 2rem;
}

.support-link {
  color: var(--p-primary-color);
  text-decoration: none;
  font-weight: 500;
}

.support-link:hover {
  text-decoration: underline;
}

.error-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.home-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-primary-contrast-color);
  background: var(--p-primary-color);
  border-radius: var(--p-content-border-radius);
  text-decoration: none;
  transition: background 0.15s ease;
}

.home-btn:hover {
  background: var(--p-primary-hover-color);
}

.play-btn {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  color: var(--p-surface-600);
  background: none;
  border: 1px solid var(--p-surface-200);
  border-radius: var(--p-content-border-radius);
  cursor: pointer;
  transition: all 0.15s ease;
}

:root.dark .play-btn {
  color: var(--p-surface-400);
  border-color: var(--p-surface-700);
}

.play-btn:hover {
  color: var(--p-surface-800);
  border-color: var(--p-surface-400);
  background: var(--p-surface-50);
}

:root.dark .play-btn:hover {
  color: var(--p-surface-200);
  border-color: var(--p-surface-500);
  background: var(--p-surface-800);
}

/* === GAME WRAPPER === */
.game-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1.25rem;
}

.tv-setup {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 900px);
}

/* === CRT BODY === */
.crt-body {
  width: 100%;
  background: linear-gradient(
    180deg,
    var(--p-surface-700) 0%,
    var(--p-surface-800) 6%,
    var(--p-surface-800) 94%,
    var(--p-surface-900) 100%
  );
  border-radius: 14px 14px 6px 6px;
  padding: 0 0.75rem 0.75rem;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.crt-top-vent {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px 0 4px;
}

.vent-slot {
  width: 24px;
  height: 3px;
  border-radius: 1px;
  background: var(--p-surface-900);
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.4);
}

.crt-inner {
  display: flex;
  gap: 0.75rem;
}

/* === SCREEN === */
.screen-well {
  flex: 1;
  background: var(--p-surface-950);
  border-radius: 8px;
  padding: 10px;
}

.screen-bezel {
  background: #0a0a08;
  border-radius: 10px;
  padding: 6px;
  box-shadow:
    inset 0 0 20px rgba(0, 0, 0, 0.9),
    inset 0 0 3px rgba(0, 0, 0, 0.5);
}

.screen-glass {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.game-screen {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border: none;
  position: relative;
  z-index: 1;
}

.scanlines {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.08) 2px,
    rgba(0, 0, 0, 0.08) 4px
  );
}

.screen-curve {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  border-radius: 8px;
  box-shadow:
    inset 0 0 80px rgba(0, 0, 0, 0.35),
    inset 0 0 20px rgba(0, 0, 0, 0.2);
}

.screen-flicker {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(218, 242, 191, 0.01) 50%,
    transparent 100%
  );
  animation: flicker 8s linear infinite;
}

/* === SIDE PANEL === */
.side-panel {
  width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  gap: 0.75rem;
}

.brand-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.brand-name {
  font-family: 'Courier New', monospace;
  font-size: 0.65rem;
  font-weight: bold;
  letter-spacing: 0.3em;
  color: var(--p-surface-500);
}

.brand-model {
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  font-weight: bold;
  letter-spacing: 0.15em;
  color: #ff4444;
  text-shadow: 0 0 8px rgba(255, 68, 68, 0.3);
}

.channel-knob {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.knob-outer {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 40% 35%,
    var(--p-surface-600),
    var(--p-surface-900)
  );
  border: 2px solid var(--p-surface-700);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.4),
    inset 0 1px 1px rgba(255, 255, 255, 0.05);
}

.knob-small {
  width: 28px;
  height: 28px;
}

.knob-inner {
  width: 60%;
  height: 60%;
  border-radius: 50%;
  position: relative;
}

.knob-line {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 50%;
  background: var(--p-surface-400);
  transform: translateX(-50%);
  border-radius: 1px;
}

.knob-label {
  font-family: 'Courier New', monospace;
  font-size: 0.45rem;
  letter-spacing: 0.15em;
  color: var(--p-surface-500);
  text-transform: uppercase;
}

.power-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.power-led {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #44bb44;
  box-shadow: 0 0 6px rgba(68, 187, 68, 0.6);
  animation: led-pulse 3s ease-in-out infinite;
}

/* === TV LEGS === */
.tv-legs {
  display: flex;
  justify-content: center;
  gap: 60%;
  width: 100%;
}

.leg {
  width: 3px;
  height: 24px;
  background: var(--p-surface-700);
  border-radius: 0 0 2px 2px;
}

.leg-left {
  transform: rotate(-8deg);
  transform-origin: top center;
}

.leg-right {
  transform: rotate(8deg);
  transform-origin: top center;
}

/* === GAME FOOTER === */
.game-footer {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.back-btn {
  font-size: 0.85rem;
  color: var(--p-surface-400);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  transition: color 0.15s ease;
}

.back-btn:hover {
  color: var(--p-surface-200);
}

.home-link {
  font-size: 0.85rem;
  color: var(--p-surface-400);
  text-decoration: none;
  transition: color 0.15s ease;
}

.home-link:hover {
  color: var(--p-surface-200);
}

/* === TV-ON TRANSITION === */
.tv-on-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.tv-on-leave-active {
  transition: all 0.25s ease-in;
}

.tv-on-enter-from {
  opacity: 0;
  transform: scaleY(0.01) scaleX(0.5);
}

.tv-on-leave-to {
  opacity: 0;
  transform: scaleY(0.01) scaleX(0.5);
}

/* === ANIMATIONS === */
@keyframes flicker {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(-100%);
  }
}

@keyframes led-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* === RESPONSIVE === */
@media (max-width: 640px) {
  .error-code {
    font-size: 4rem;
  }

  .side-panel {
    width: 60px;
  }

  .knob-outer {
    width: 28px;
    height: 28px;
  }

  .knob-small {
    width: 22px;
    height: 22px;
  }

  .brand-model {
    font-size: 0.85rem;
  }
}

@media (min-height: 800px) {
  .tv-setup {
    width: min(100%, 1000px);
  }
}

@media (min-height: 1000px) {
  .tv-setup {
    width: min(100%, 1100px);
  }

  .crt-body {
    padding: 0 1rem 1rem;
  }

  .screen-well {
    padding: 14px;
  }

  .side-panel {
    width: 100px;
  }

  .knob-outer {
    width: 44px;
    height: 44px;
  }

  .knob-small {
    width: 34px;
    height: 34px;
  }

  .brand-model {
    font-size: 1.3rem;
  }

  .brand-name {
    font-size: 0.75rem;
  }
}
</style>
